import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { Download, Search } from "@mui/icons-material";
import { useEffect, useState } from "react";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";
import {
  openMeetingInviteForm,
  fetchMeetingInvitesByMeetingId,
} from "../../../redux/slices/meetingInvite";
import { format } from "date-fns";

import { baseUrl } from "../../../config";
import PDFViewerModal from "../../PDFViewer";

type Status =
  | "queued"
  | "pdf_generating"
  | "ready"
  | "sending_email"
  | "sending_post"
  | "sent"
  | "failed"
  | undefined;

function getInviteStatusInfo(status: Status): {
  label: string;
  color: ChipProps["color"];
  variant?: "filled" | "outlined";
} {
  switch (status) {
    case "queued":
      return { label: "Jonossa", color: "default", variant: "outlined" };
    case "pdf_generating":
      return { label: "Luodaan PDF:ää", color: "info", variant: "outlined" };
    case "ready":
      return { label: "PDF valmis", color: "info", variant: "filled" };
    case "sending_email":
      return {
        label: "Lähetetään (sähköposti)",
        color: "warning",
        variant: "filled",
      };
    case "sending_post":
      return {
        label: "Lähetetään (kirje)",
        color: "warning",
        variant: "filled",
      };
    case "sent":
      return { label: "Lähetetty", color: "success", variant: "filled" };
    case "failed":
      return { label: "Epäonnistui", color: "error", variant: "filled" };
    default:
      return { label: "Tuntematon", color: "default", variant: "outlined" };
  }
}

function MeetingInvite({
  meetingId,
}: {
  meetingId?: number;
  hideHeader?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { status: associationStatus } = useAppSelector(
    (state) => state.association
  );
  const { meetingInvites, status: inviteStatus } = useAppSelector(
    (state) => state.meetingInvite
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const handleShowInvite = (invite: any, isOpen: boolean) => {
    const filename = invite.file?.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  const invite = meetingInvites[0];

  const isLoading =
    associationStatus === "loading" || inviteStatus === "loading";

  const handleDownload = async (rowFile: any) => {
    const filename = rowFile.file?.filename;
    if (!filename) return;

    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  useEffect(() => {
    if (meetingId) {
      dispatch(fetchMeetingInvitesByMeetingId(meetingId));
    }

    return () => {
      dispatch(openMeetingInviteForm(false));
    };
  }, [dispatch, currentUser?.associations, meetingId]);

  return (
    <>
      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      <Card sx={{ mb: 6 }}>
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Kokouskutsut
            </Typography>
          </Box>
        </CardContent>

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nimi</TableCell>
                <TableCell>Lähetys Pvm</TableCell>
                <TableCell>Kutsumistapa</TableCell>
                <TableCell>Tila</TableCell>
                <TableCell>Toiminnot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="50vh"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : meetingInvites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography>Ei kutsuja</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                meetingInvites.map((row: any) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {`${row.user.firstName} ${row.user.lastName} `}
                    </TableCell>

                    <TableCell>
                      {row.sentAt
                        ? format(new Date(row.sentAt), "dd.MM.yyyy HH.mm")
                        : "Viestiä ei lähetetty"}
                    </TableCell>
                    <TableCell>
                      {row.user.invoiceDeliveryMethod === "EMAIL"
                        ? "Sähköposti"
                        : row.user.invoiceEmail
                          ? `Kirje + Sähköposti (${row.user.invoiceEmail})`
                          : "Kirje"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const { label, color, variant } = getInviteStatusInfo(
                          row.status as Status
                        );
                        return (
                          <Chip
                            size="small"
                            label={label}
                            color={color}
                            variant={variant}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Download
                        color={!row.file?.filename ? "disabled" : "action"}
                        style={{
                          cursor: !row.file?.filename
                            ? "not-allowed"
                            : "pointer",
                        }}
                        onClick={
                          !row.file?.filename
                            ? undefined
                            : () => handleDownload(row)
                        }
                      />
                      <Search
                        color={!row.file?.filename ? "disabled" : "action"}
                        onClick={
                          !row.file?.filename
                            ? undefined
                            : () => handleShowInvite(row, true)
                        }
                        style={{
                          cursor: !row.file?.filename
                            ? "not-allowed"
                            : "pointer",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Card>
    </>
  );
}

export default MeetingInvite;
